import { useCallback, useEffect, useState, type ReactElement } from "react";
import { createRoute } from "@tanstack/react-router";
import { Route as RootRoute } from "../__root";
import { ConsentStepper } from "../../components/custom/ConsentStepper";
import { renderConsentMarkdown } from "../../lib/markdownRender";
import type { ConsentAcceptPayload } from "../../schemas/consent";

/**
 * /consent — onboarding flow.
 *
 * Loadne current consent text version (GET /api/tenant/consent/required) a
 * vykreslí 4-step Wizard přes [ConsentStepper]. Po POST /api/tenant/consent/accept
 * naviguje na /dashboard (TanStack Router přijde v Phase 7).
 */

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/consent",
  component: () => <ConsentOnboardingRoute />,
});

interface RequiredResponse {
  requires_consent_re_prompt: boolean;
  latest_material_version: string | null;
  user_consent_text_version: string | null;
  /** V049 + spec-proposal DEC-013 G. Null = žádná material verze, "" = legacy bez body. */
  body_markdown: string | null;
}

export function ConsentOnboardingRoute(): ReactElement {
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState<string | null>(null);
  const [text, setText] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/tenant/consent/required");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as RequiredResponse;
        if (cancelled) return;
        setVersion(data.latest_material_version);
        // Render body from authoritative DB. Pokud verze nemá body (legacy
        // pre-V049 row), padáme na fallback placeholder s warning bannerem —
        // admin musí republishnout reálný text (spec-proposal DEC-013 G).
        if (data.body_markdown && data.body_markdown.trim()) {
          setText(renderConsentMarkdown(data.body_markdown));
        } else {
          setText(buildLegacyFallback(data.latest_material_version ?? "current"));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccept = useCallback(async (payload: ConsentAcceptPayload) => {
    const r = await fetch("/api/tenant/consent/accept", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": readCsrfCookie() ?? "",
      },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const errBody = (await r.json().catch(() => ({}))) as { message?: string };
      throw new Error(errBody.message ?? `HTTP ${r.status}`);
    }
  }, []);

  if (loading) {
    return (
      <main className="mx-auto my-8 max-w-[800px] bg-primary p-6 text-primary" aria-busy="true">
        <p>Načítám…</p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="mx-auto my-8 max-w-[800px] bg-primary p-6 text-primary" role="alert">
        <p>Chyba: {error}</p>
      </main>
    );
  }
  if (!version) {
    return (
      <main className="mx-auto my-8 max-w-[800px] bg-primary p-6 text-primary">
        <p>Žádná verze consent textu k udělení.</p>
      </main>
    );
  }
  return (
    <main className="mx-auto my-8 max-w-[800px] bg-primary p-6 text-primary">
      <ConsentStepper
        consentTextVersion={version}
        consentTextHtml={text}
        onAccept={handleAccept}
      />
    </main>
  );
}

function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)axigon_csrf=([^;]+)/);
  return match?.[1] ?? null;
}

function buildLegacyFallback(version: string): string {
  // Fallback pro consent_text_versions row bez body_markdown (V049 migration
  // backfill — empty default). Po republish s reálným textem se použije body z DB.
  return `
    <div role="alert" style="background:#fff3cd;border:1px solid #ffe58f;padding:0.75rem;border-radius:4px;margin-bottom:1rem">
      <strong>Text souhlasu zatím není k dispozici.</strong>
      Tato verze byla vytvořena dříve, než přibyl povinný text souhlasu.
      Kontaktuj administrátora — verzi je potřeba znovu publikovat s textací.
    </div>
    <h2>Souhlas se zpracováním osobních údajů</h2>
    <p>Verze ${version}.</p>
  `.trim();
}
